import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "../context/SubscriptionContext";
import { normalizeClinicId } from "../utils/clinicIdUtils";
import { Clock3, FileText, History, Mars, Search, SearchX, UserCog, UserRound, UsersRound, Venus, VenusAndMars, XCircle, ClipboardList } from "lucide-react";
import { buildApiUrl } from '../services/apiHelpers';

function PatientsPage() {
  const navigate = useNavigate();
  const { clinicInfo } = useSubscription();
  const clinicId = normalizeClinicId(clinicInfo?.id || null);

  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const safeSetFilteredPatients = (value) => setFilteredPatients(Array.isArray(value) ? value : []);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const [showHistory, setShowHistory] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientHistory, setPatientHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const safePatients = Array.isArray(patients) ? patients : [];
  const safeFilteredPatients = Array.isArray(filteredPatients) ? filteredPatients : [];
  const safePatientHistory = Array.isArray(patientHistory) ? patientHistory : [];
  const renderPatients = safeFilteredPatients;

  /* ================= FETCH PATIENTS ================= */
  useEffect(() => {
    if (!clinicId) {
      setPatients([]);
      setFilteredPatients([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    fetch(buildApiUrl(`clinic/patients/${clinicId}`))
      .then(res => res.json())
      .then(data => {
        const patientsArray = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        setPatients(patientsArray);
        safeSetFilteredPatients(patientsArray);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setPatients([]);
        safeSetFilteredPatients([]);
        setLoading(false);
      });
  }, [clinicId]);

  /* ================= SEARCH FILTER ================= */
  useEffect(() => {
    const sourcePatients = Array.isArray(patients) ? patients : [];
    if (!search) {
      safeSetFilteredPatients(sourcePatients);
      return;
    }
    const lower = search.toLowerCase();
    const filtered = sourcePatients.filter(p =>
      p.patientName?.toLowerCase().includes(lower) ||
      p.phone?.includes(search)
    );
    safeSetFilteredPatients(filtered);
  }, [search, patients]);

  /* ================= LOAD HISTORY ================= */
  const openHistory = async (patient) => {
    if (!clinicId || !patient?.id) {
      console.warn("Cannot load history without a valid clinicId and patient id", { clinicId, patient });
      return;
    }

    setSelectedPatient(patient);
    setShowHistory(true);
    setPatientHistory([]);
    setHistoryLoading(true);
    try {
      const res = await fetch(
        buildApiUrl(`prescriptions/patient/${patient.id}`),
        { method: "GET", headers: { "X-Clinic-Id": clinicId } }
      );
      const data = await res.json();
      const historyArray = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      setPatientHistory(historyArray);
    } catch (err) {
      console.error(err);
      setPatientHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openReports = async (patient) => {
    if (patient?.id) navigate(`/admin-dashboard/patients/${patient.id}/reports`);
  };

  /* ================= HELPERS ================= */
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      const normalized = String(dateString);
      const date = /^\d{4}-\d{2}-\d{2}$/.test(normalized)
        ? new Date(`${normalized}T00:00:00+05:30`)
        : new Date(normalized);
      if (Number.isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString("en-IN", {
        year: "numeric", month: "short", day: "numeric"
      , timeZone: "Asia/Kolkata" });
    } catch {
      return dateString;
    }
  };

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

    *, *::before, *::after { box-sizing: border-box; }

    .pp-root {
      font-family: var(--cq-font-family, 'Inter', system-ui, sans-serif);
      padding: 8px 0 12px;
      width: 100%;
      max-width: none;
      margin: 0;
      color: var(--cq-color-text, #0f172a);
      min-height: 0;
      height: 100%;
      overflow: hidden;
      background: transparent;
      display: flex;
      flex-direction: column;
    }

    /* ── HEADER ── */
    .pp-header {
      margin-bottom: 0;
    }

    .pp-header-box {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
      padding: 10px 8px 8px;
      border-bottom: 1px solid var(--cq-color-border, rgba(191,219,254,0.8));
      background:
        linear-gradient(180deg, rgba(255,255,255,0.68) 0%, rgba(239,246,255,0.28) 100%);
    }

    .pp-title {
      display: flex;
      align-items: center;
      gap: 14px;
    }

    .pp-title-icon {
      width: 48px;
      height: 48px;
      border-radius: 14px;
      background: linear-gradient(135deg, #1e40af 0%, #2563eb 45%, #3b82f6 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      box-shadow:
        0 10px 24px rgba(37,99,235,0.22),
        0 0 0 1px rgba(191,219,254,0.55) inset;
    }

    .pp-title-icon svg {
      width: 24px;
      height: 24px;
      color: white;
    }

    .pp-title h2 {
      margin: 0 0 3px;
      font-size: 28px;
      font-weight: 800;
      letter-spacing: -0.4px;
      color: var(--cq-color-text, #0f172a);
      line-height: 1.1;
    }

    .pp-title p {
      margin: 0;
      font-size: 13px;
      color: var(--cq-color-text-secondary, #64748b);
      font-weight: 500;
    }

    .pp-badge {
      display: flex;
      align-items: center;
      gap: 8px;
      background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 55%, #3b82f6 100%);
      color: var(--cq-color-text-inverse, white);
      padding: 10px 20px;
      border-radius: 40px;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.02em;
      box-shadow:
        0 12px 28px rgba(37,99,235,0.24),
        0 0 0 1px rgba(191,219,254,0.3) inset;
      white-space: nowrap;
    }

    .pp-badge-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #bfdbfe;
      flex-shrink: 0;
    }

    /* ── MAIN CARD (search + table combined) ── */
    .pp-card {
      background: transparent;
      border-radius: 0;
      box-shadow: none;
      overflow: hidden;
      margin-bottom: 0;
      display: flex;
      flex-direction: column;
      min-height: 0;
      flex: 1;
    }

    /* ── SEARCH BAR ── */
    .pp-search-bar {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 14px;
      background: #ffffff;
      border: 1px solid rgba(226,234,249,0.9);
      border-radius: 12px;
      box-shadow: 0 8px 20px rgba(15,23,42,0.04);
      margin: 10px 8px;
    }

    .pp-search-icon {
      color: var(--cq-color-primary, #1d4ed8);
      flex-shrink: 0;
      width: 18px;
      height: 18px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      opacity: 0.95;
    }

    .pp-search-input {
      flex: 1;
      border: none;
      outline: none;
      background: transparent;
      font-size: 15px;
      font-family: inherit;
      color: var(--cq-color-text, #0f172a);
      font-weight: 600;
      padding: 6px 0;
      min-width: 0;
    }

    .pp-search-input::placeholder {
      color: var(--cq-color-text-tertiary, #94a3b8);
      font-weight: 500;
    }

    .pp-search-count {
      font-size: 12px;
      font-weight: 700;
      color: var(--cq-color-text-inverse, #fff);
      background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%);
      padding: 6px 12px;
      border-radius: 999px;
      white-space: nowrap;
      flex-shrink: 0;
      box-shadow: 0 6px 20px rgba(37,99,235,0.12);
    }

    /* ── TABLE ── */
    .pp-table-wrap {
      flex: 1;
      min-height: 0;
      overflow: auto;
      -webkit-overflow-scrolling: touch;
      padding: 0 8px 8px;
    }

    .pp-table {
      width: 100%;
      min-width: 600px;
      border-collapse: collapse;
      font-size: 14px;
    }

    /* Mobile card list (hidden on desktop) */
    .pp-card-list { display: none; flex-direction: column; gap: 14px; padding: 8px; }

    .pp-mobile-card {
      background: rgba(255,255,255,0.98);
      border-radius: 14px;
      border: 1px solid rgba(37,99,235,0.08);
      padding: 14px 12px;
      box-shadow: 0 14px 36px rgba(37,99,235,0.06);
      display: flex;
      flex-direction: column;
      gap: 10px;
      width: 100%;
      overflow: hidden;
    }

    .pp-mobile-row { display:flex; justify-content:space-between; gap:12px; font-size:13.5px; }
    .pp-mobile-label { color: var(--cq-color-text-tertiary, #94a3b8); font-weight:600; }
    .pp-mobile-val { font-weight:700; color: var(--cq-color-text, #0f172a); }


    /* highlighted header */
    .pp-table thead tr {
      background: linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 55%, #2563eb 100%);
      position: sticky;
      top: 0;
      z-index: 2;
    }

    .pp-table thead th {
      padding: 14px 20px;
      text-align: center;
      font-size: 11.5px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--cq-color-text-inverse, rgba(255,255,255,0.88));
      white-space: nowrap;
    }

    .pp-table thead th:not(.pp-th-num) {
      letter-spacing: 0.1em;
    }

    .pp-table thead th {
      box-shadow: 0 1px 0 rgba(255,255,255,0.08) inset;
    }

    .pp-table thead th:first-child { border-radius: 0; }

    .pp-table tbody tr {
      border-bottom: 1px solid var(--cq-color-border-light, #eef2fb);
      transition: background 0.15s ease;
    }

    .pp-table tbody tr:last-child {
      border-bottom: none;
    }

    .pp-table tbody tr:hover {
      background: linear-gradient(90deg, rgba(239,246,255,0.95) 0%, rgba(255,255,255,0.98) 100%);
    }

    .pp-table td {
      padding: 15px 20px;
      color: var(--cq-color-text-secondary, #334155);
      vertical-align: middle;
      text-align: center;
    }

    .pp-avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--cq-color-bg, #dbeafe), var(--cq-color-border, #bfdbfe));
      color: var(--cq-color-primary, #1d4ed8);
      font-weight: 700;
      font-size: 13px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      letter-spacing: 0.03em;
    }

    .pp-name-cell {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 12px;
    }

    .pp-table td:nth-child(2) {
      text-align: left;
    }

    .pp-name-text {
      font-weight: 600;
      color: var(--cq-color-text, #0f172a);
      font-size: 14px;
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: break-word;
      max-width: 100%;
      display: block;
    }

    .pp-phone {
      font-family: var(--cq-font-mono, 'SF Mono', 'Fira Code', monospace);
      font-size: 13px;
      color: var(--cq-color-text-secondary, #475569);
      letter-spacing: 0.02em;
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    .pp-age-badge {
      display: inline-block;
      background: var(--cq-color-bg, #eff6ff);
      color: var(--cq-color-primary, #1d4ed8);
      border-radius: 8px;
      padding: 3px 10px;
      font-weight: 600;
      font-size: 13px;
    }

    .pp-gender-m {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.04em;
      color: var(--cq-color-primary, #1d4ed8);
      background: linear-gradient(135deg, rgba(239,246,255,0.95) 0%, rgba(219,234,254,0.95) 100%);
      border-radius: 20px;
      padding: 4px 10px;
      border: 1px solid rgba(191,219,254,0.6);
    }

    .pp-gender-f {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.04em;
      color: #db2777;
      background: linear-gradient(135deg, rgba(253,242,248,0.96) 0%, rgba(252,231,243,0.96) 100%);
      border-radius: 20px;
      padding: 4px 10px;
      border: 1px solid rgba(251,207,232,0.8);
    }

    .pp-gender-o {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.04em;
      color: #6d28d9;
      background: linear-gradient(135deg, rgba(245,243,255,0.96) 0%, rgba(237,233,254,0.96) 100%);
      border-radius: 20px;
      padding: 4px 10px;
      border: 1px solid rgba(221,214,254,0.85);
    }

    .pp-history-btn {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      padding: 8px 16px;
      border-radius: 10px;
      border: 1.5px solid rgba(59,130,246,0.9);
      background: linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(239,246,255,0.85) 100%);
      color: var(--cq-color-primary, #2563eb);
      font-size: 12.5px;
      font-weight: 700;
      font-family: inherit;
      cursor: pointer;
      transition: all 0.18s ease;
      white-space: nowrap;
      letter-spacing: 0.02em;
    }

    .pp-history-btn:hover {
      background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 60%, #3b82f6 100%);
      color: var(--cq-color-text-inverse, white);
      box-shadow: 0 12px 24px rgba(37,99,235,0.22);
      transform: translateY(-1px);
    }

    .pp-history-btn svg {
      width: 14px;
      height: 14px;
    }

    .pp-record-actions {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-wrap: wrap;
      gap: 7px;
      width: 100%;
    }

    @media (max-width: 700px) {
      .pp-mobile-row {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
      }

      .pp-mobile-label {
        font-size: 12px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }

      .pp-record-actions {
        gap: 8px;
      }

      .pp-record-actions .pp-history-btn {
        flex: 1 1 0;
        justify-content: center;
        min-width: 0;
        padding-inline: 8px;
      }
    }

    .pp-report-btn {
      border-color: rgba(13,148,136,0.75);
      color: #0f766e;
      background: linear-gradient(180deg, rgba(240,253,250,0.95) 0%, rgba(204,251,241,0.82) 100%);
    }

    .pp-report-btn:hover {
      background: linear-gradient(135deg, #0f766e 0%, #0d9488 60%, #14b8a6 100%);
      box-shadow: 0 12px 24px rgba(13,148,136,0.2);
    }

    .pp-empty, .pp-loading {
      text-align: center;
      padding: 64px 20px;
      color: var(--cq-color-text-tertiary, #94a3b8);
    }

    .pp-loading-spinner {
      width: 36px;
      height: 36px;
      border: 3px solid var(--cq-color-border, #e2eaf4);
      border-top-color: var(--cq-color-primary, #2563eb);
      border-radius: 50%;
      animation: pp-spin 0.7s linear infinite;
      margin: 0 auto 14px;
    }

    @keyframes pp-spin {
      to { transform: rotate(360deg); }
    }

    .pp-empty-icon {
      font-size: 40px;
      margin-bottom: 12px;
    }

    .pp-empty p { margin: 6px 0 0; font-size: 14px; font-weight: 500; color: var(--cq-color-text-tertiary, #94a3b8); }
    .pp-empty strong { display: block; font-size: 16px; color: var(--cq-color-text-secondary, #64748b); margin-bottom: 4px; }

    /* ── MODAL ── */
    .pp-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.52);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 16px;
      z-index: 2000;
      animation: pp-fade-in 0.2s ease;
    }

    @keyframes pp-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .pp-modal {
      width: min(100%, 880px);
      max-height: 90vh;
      background: var(--cq-color-surface, #ffffff);
      border-radius: 22px;
      box-shadow: 0 40px 80px rgba(10,20,50,0.18);
      display: flex;
      flex-direction: column;
      animation: pp-slide-up 0.25s cubic-bezier(0.34,1.56,0.64,1);
      overflow: hidden;
    }

    @keyframes pp-slide-up {
      from { transform: translateY(30px); opacity: 0; }
      to   { transform: translateY(0);    opacity: 1; }
    }

    .pp-modal-header {
      padding: 22px 28px 18px;
      border-bottom: 1px solid var(--cq-color-border, #e2eaf4);
      background: linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 55%, #2563eb 100%);
      flex-shrink: 0;
    }

    .pp-modal-header-top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
    }

    .pp-modal-title {
      margin: 0;
      font-size: 20px;
      font-weight: 800;
      color: var(--cq-color-text-inverse, #ffffff);
      letter-spacing: -0.3px;
    }

    .pp-modal-subtitle {
      margin: 4px 0 0;
      font-size: 12.5px;
      color: rgba(255,255,255,0.72);
      font-weight: 500;
    }

    .pp-modal-close {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      border: none;
      background: rgba(255,255,255,0.12);
      color: var(--cq-color-text-inverse, white);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      flex-shrink: 0;
      font-size: 20px;
      line-height: 1;
      transition: background 0.15s ease;
    }

    .pp-modal-close:hover {
      background: rgba(255,255,255,0.25);
    }

    .pp-modal-body {
      overflow-y: auto;
      flex: 1;
    }

    /* History table */
    .pp-hist-wrap {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    .pp-hist-table {
      width: 100%;
      min-width: 560px;
      border-collapse: collapse;
      font-size: 13.5px;
    }

    .pp-hist-table thead tr {
      background: linear-gradient(135deg, rgba(239,246,255,0.98) 0%, rgba(219,234,254,0.98) 100%);
      position: sticky;
      top: 0;
      z-index: 1;
    }

    .pp-hist-table thead th {
      padding: 13px 18px;
      text-align: left;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.07em;
      text-transform: uppercase;
      color: var(--cq-color-primary, #1d4ed8);
      border-bottom: 2px solid var(--cq-color-border, #bfdbfe);
      white-space: nowrap;
    }

    .pp-hist-table tbody tr {
      border-bottom: 1px solid var(--cq-color-border-light, #eef2fb);
      transition: background 0.12s ease;
    }

    .pp-hist-table tbody tr:last-child { border-bottom: none; }
    .pp-hist-table tbody tr:hover { background: var(--cq-color-surface-hover, #f8fbff); }

    .pp-hist-table td {
      padding: 14px 18px;
      color: var(--cq-color-text-secondary, #334155);
      vertical-align: middle;
    }

    .pp-hist-date {
      font-weight: 600;
      color: var(--cq-color-primary-dark, #1e3a8a);
      font-size: 13px;
      white-space: nowrap;
    }

    .pp-hist-doctor {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-weight: 600;
      color: var(--cq-color-text, #0f172a);
    }

    .pp-hist-diagnosis {
      background: var(--cq-color-surface-hover, #f1f5f9);
      color: var(--cq-color-text-secondary, #334155);
      border-radius: 6px;
      padding: 4px 10px;
      font-size: 12.5px;
      font-weight: 500;
      display: inline-block;
    }

    .pp-hist-no-diagnosis {
      color: var(--cq-color-text-tertiary, #94a3b8);
      font-style: italic;
      font-size: 12.5px;
    }

    .pp-rx-link {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      color: var(--cq-color-primary, #2563eb);
      font-weight: 600;
      font-size: 12.5px;
      text-decoration: none;
      padding: 5px 12px;
      border-radius: 8px;
      border: 1.5px solid var(--cq-color-border, #bfdbfe);
      transition: all 0.15s ease;
      white-space: nowrap;
      background: linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(239,246,255,0.9) 100%);
    }

    .pp-rx-link:hover {
      background: var(--cq-color-bg, #eff6ff);
      border-color: var(--cq-color-primary, #2563eb);
    }

    .pp-no-rx {
      color: var(--cq-color-text-disabled, #cbd5e1);
      font-size: 12px;
      font-style: italic;
    }

    .pp-hist-empty {
      text-align: center;
      padding: 52px 20px;
      color: var(--cq-color-text-tertiary, #94a3b8);
    }

    .pp-hist-loading {
      text-align: center;
      padding: 52px 20px;
      color: var(--cq-color-text-secondary, #64748b);
    }

    .pp-modal-footer {
      padding: 16px 28px;
      border-top: 1px solid var(--cq-color-border, #e2eaf4);
      display: flex;
      justify-content: flex-end;
      background: var(--cq-color-bg, #fafbff);
      flex-shrink: 0;
    }

    .pp-close-btn {
      padding: 10px 24px;
      border-radius: 12px;
      border: none;
      background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 60%, #3b82f6 100%);
      color: var(--cq-color-text-inverse, white);
      font-weight: 700;
      font-size: 13.5px;
      font-family: inherit;
      cursor: pointer;
      transition: all 0.18s ease;
      box-shadow: 0 3px 10px var(--cq-color-primary-glow, rgba(29, 78, 216, 0.16));
    }

    .pp-close-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px var(--cq-color-primary-glow, rgba(29, 78, 216, 0.16));
    }

    /* serial number col */
    .pp-table thead th.pp-th-num,
    .pp-table td.pp-td-num {
      width: 50px;
      color: rgba(255,255,255,0.72);
      font-size: 11px;
    }
    .pp-table td.pp-td-num {
      color: var(--cq-color-text-tertiary, #94a3b8);
      font-size: 13px;
      font-weight: 500;
    }

    /* ── RESPONSIVE ── */
    @media (max-width: 1024px) {
      .pp-header-box,
      .pp-search-bar,
      .pp-table-wrap { padding-left: 8px; padding-right: 8px; }
    }

    @media (max-width: 1024px) {
      .pp-search-bar { margin: 8px; padding: 10px 12px; }
      .pp-search-input { font-size: 15px; }
      .pp-search-count { padding: 5px 10px; font-size: 12px; }
    }

    @media (max-width: 768px) {
      .pp-header-box,
      .pp-search-bar,
      .pp-table-wrap { padding-left: 6px; padding-right: 6px; }
      .pp-header-box { flex-direction: column; align-items: stretch; }
      .pp-badge { width: 100%; justify-content: center; }
      .pp-table thead th.pp-th-num,
      .pp-table td.pp-td-num { display: none; }
      .pp-modal { border-radius: 18px; }
      /* hide desktop table on small screens, show mobile cards */
      .pp-table { display: none; }
      .pp-card-list { display: flex; }
    }

    @media (max-width: 480px) {
      .pp-header-box,
      .pp-search-bar,
      .pp-table-wrap { padding-left: 4px; padding-right: 4px; }
      .pp-header-box { padding-top: 8px; padding-bottom: 6px; }
      .pp-title h2 { font-size: 22px; }
      .pp-modal-header { padding: 18px 20px 14px; }
      .pp-modal-title { font-size: 17px; }
      .pp-modal-footer { padding: 14px 20px; }
      .pp-modal { border-radius: 16px; max-height: 95vh; }
      .pp-hist-table { min-width: 460px; }
    }

    @media (prefers-reduced-motion: reduce) {
      .pp-backdrop, .pp-modal, .pp-loading-spinner { animation: none; }
    }
  `;

  const getInitials = (name = "") =>
    name.split(" ").slice(0, 2).map(w => w[0]?.toUpperCase()).join("");

  const genderTag = (g) => {
    const val = g?.toUpperCase();
    if (val === "MALE")   return <span className="pp-gender-m"><Mars size={13} strokeWidth={2.4} /> Male</span>;
    if (val === "FEMALE") return <span className="pp-gender-f"><Venus size={13} strokeWidth={2.4} /> Female</span>;
    return <span className="pp-gender-o"><VenusAndMars size={13} strokeWidth={2.4} /> {g || "Other"}</span>;
  };

  return (
    <div className="pp-root">
      <style>{css}</style>

      <div className="pp-card">
        {/* ── HEADER ── */}
        <div className="pp-header-box">
          <div className="pp-title">
            <div className="pp-title-icon">
              <UserRound size={24} strokeWidth={2.2} />
            </div>
            <div>
              <h2>Patients</h2>
              <p>Manage and view all registered clinic patients</p>
            </div>
          </div>
          <div className="pp-badge">
            <UsersRound size={14} strokeWidth={2.2} />
            {safePatients.length} Total Patients
          </div>
        </div>
        {/* Search bar */}
        <div className="pp-search-bar">
          <Search className="pp-search-icon" strokeWidth={2.2} />
          <input
            className="pp-search-input"
            type="text"
            placeholder="Search by patient name or phone number…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <span className="pp-search-count">
              {safeFilteredPatients.length} result{safeFilteredPatients.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div className="pp-loading">
            <div className="pp-loading-spinner" />
            Loading patients…
          </div>
        ) : safeFilteredPatients.length === 0 ? (
          <div className="pp-empty">
            <SearchX className="pp-empty-icon" size={44} strokeWidth={2} />
            <strong>{search ? "No results found" : "No patients yet"}</strong>
            <p>{search ? `No patients match "${search}"` : "Registered patients will appear here."}</p>
          </div>
        ) : (
          <div className="pp-table-wrap">
            <table className="pp-table">
              <thead>
                <tr>
                  <th className="pp-th-num">#</th>
                  <th>Patient Name</th>
                  <th>Phone</th>
                  <th>Age</th>
                  <th>Gender</th>
                  <th>Records</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(renderPatients)
                  ? renderPatients.map((p, i) => (
                      <tr key={p.id}>
                        <td className="pp-td-num">{i + 1}</td>
                        <td>
                          <div className="pp-name-cell">
                            <div className="pp-avatar">{getInitials(p.patientName)}</div>
                            <span className="pp-name-text">{p.patientName}</span>
                          </div>
                        </td>
                        <td><span className="pp-phone">+91 {p.phone}</span></td>
                        <td><span className="pp-age-badge">{p.age} yrs</span></td>
                        <td>{genderTag(p.gender)}</td>
                        <td>
                          <div className="pp-record-actions">
                            <button className="pp-history-btn" onClick={() => openHistory(p)}>
                              <History size={14} strokeWidth={2.2} /> View History
                            </button>
                            <button className="pp-history-btn pp-report-btn" onClick={() => openReports(p)}>
                              <FileText size={14} strokeWidth={2.2} /> Reports
                            </button>
                          </div>
                        </td>
                      </tr>
                    )) : null}
              </tbody>
            </table>
            {/* Mobile card list (visible on small screens) */}
            <div className="pp-card-list">
              {Array.isArray(renderPatients)
                ? renderPatients.map((p, i) => (
                    <div className="pp-mobile-card" key={p.id}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
                        <div style={{display:'flex',alignItems:'center',gap:12}}>
                          <div className="pp-avatar">{getInitials(p.patientName)}</div>
                          <div>
                            <div className="pp-name-text">{p.patientName}</div>
                            <div className="pp-phone">+91 {p.phone}</div>
                          </div>
                        </div>
                        <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:6}}>
                          <div className="pp-age-badge">{p.age} yrs</div>
                          <div>{genderTag(p.gender)}</div>
                        </div>
                      </div>

                      <div className="pp-mobile-row">
                        <div className="pp-mobile-label">Records</div>
                        <div className="pp-record-actions">
                          <button className="pp-history-btn" onClick={() => openHistory(p)}>
                            <History size={14} strokeWidth={2.2} /> View History
                          </button>
                          <button className="pp-history-btn pp-report-btn" onClick={() => openReports(p)}>
                            <FileText size={14} strokeWidth={2.2} /> Reports
                          </button>
                        </div>
                      </div>
                    </div>
                  )) : null}
            </div>
          </div>
        )}
      </div>

      {/* ── HISTORY MODAL ── */}
      {showHistory && (
        <div className="pp-backdrop" onClick={(e) => e.target === e.currentTarget && setShowHistory(false)}>
          <div className="pp-modal">
            {/* Modal header */}
            <div className="pp-modal-header">
              <div className="pp-modal-header-top">
                <div>
                  <h3 className="pp-modal-title">
                    Medical History — {selectedPatient?.patientName}
                  </h3>
                  <p className="pp-modal-subtitle">All past consultations and prescriptions</p>
                </div>
                <button className="pp-modal-close" onClick={() => setShowHistory(false)} aria-label="Close">
                  <XCircle size={18} strokeWidth={2.2} />
                </button>
              </div>
            </div>

            {/* Modal body */}
            <div className="pp-modal-body">
              {historyLoading ? (
                <div className="pp-hist-loading">
                  <Clock3 size={18} strokeWidth={2.2} style={{ marginBottom: 8, color: "#2563eb" }} />
                  <div className="pp-loading-spinner" />
                  Loading history…
                </div>
              ) : safePatientHistory.length === 0 ? (
                <div className="pp-hist-empty">
                  <ClipboardList size={38} strokeWidth={1.9} style={{ marginBottom: 10, color: "#2563eb" }} />
                  <strong style={{ color: "var(--cq-color-text-secondary, #64748b)", fontSize: 15 }}>No history available</strong>
                  <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--cq-color-text-tertiary, #94a3b8)" }}>
                    No past consultations found for this patient.
                  </p>
                </div>
              ) : (
                <div className="pp-hist-wrap">
                  <table className="pp-hist-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Consultation Date</th>
                        <th>Consulting Physician</th>
                        <th>Clinical Diagnosis</th>
                        <th>Prescription</th>
                      </tr>
                    </thead>
                    <tbody>
                      {safePatientHistory.map((h, i) => (
                        <tr key={h.id}>
                          <td style={{ color: "var(--cq-color-text-tertiary, #94a3b8)", fontWeight: 600, width: 40 }}>{i + 1}</td>
                          <td>
                            <span className="pp-hist-date">
                              📅 {formatDate(h.createdAt || h.appointmentDate)}
                            </span>
                          </td>
                          <td>
                            <span className="pp-hist-doctor">
                              <UserCog size={14} strokeWidth={2.1} /> {h.doctorName || "—"}
                            </span>
                          </td>
                          <td>
                            {h.diagnosis
                              ? <span className="pp-hist-diagnosis">{h.diagnosis}</span>
                              : <span className="pp-hist-no-diagnosis">Not recorded</span>
                            }
                          </td>
                          <td>
                            {h.imageUrl
                              ? (
                                <a href={h.imageUrl} target="_blank" rel="noreferrer" className="pp-rx-link">
                                  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
                                    <path d="M4 2h6l4 4v8a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z" strokeLinejoin="round"/>
                                    <path d="M10 2v4h4" strokeLinejoin="round"/>
                                    <path d="M5 9h6M5 12h4" strokeLinecap="round"/>
                                  </svg>
                                  View Rx
                                </a>
                               )
                              : <span className="pp-no-rx">—</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="pp-modal-footer">
              <button className="pp-close-btn" onClick={() => setShowHistory(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default PatientsPage;